/**
 * Server settings tabs added for user-management P1: invites (create, copy,
 * revoke), the audit log (filters, paging, live entries), bans (unban),
 * recently deleted channels and roles (restore), and ownership (the 7-day
 * transfer). Every tab is permission-gated (`serverTools`) and every list
 * says when it is loading, empty or failed.
 */
import {
  ChannelType,
  isVoiceChannelType,
  Permissions,
  snowflakeDate,
  type AuditLogEntry,
  type Ban,
  type DeletedItem,
  type Invite,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { serverOrigin } from '../lib/config';
import { useData } from '../store/data';
import {
  can,
  channelIcon,
  channelPermissions,
  displayName,
  useActiveGuildId,
  useChannelTree,
  userName,
} from '../store/selectors';
import { onGatewayEvent } from '../store/umEvents';
import { Avatar, Btn, Icon, Select, Switch } from '../ui/kit';
import { confirmDialog, toast } from '../ui/overlay';
import { runAction } from '../mod/actions';
import { openModDialog } from '../mod/ModDialogs';
import { AUDIT_FILTERS, auditLine, auditReason, auditSourceKey, auditUser } from './auditText';

type Load<T> = { st: 'loading' } | { st: 'error' } | { st: 'ok'; value: T };

function useGid(): Snowflake | null {
  return useActiveGuildId();
}

function Loading() {
  const { t } = useT();
  return (
    <p className="ns-muted ns-pad">
      <i className="nx-spin" /> {t('ns.loading')}
    </p>
  );
}

/* ---------------- invites ---------------- */

const AGES = [
  { key: 'um.invites.age.30m', s: 1800 },
  { key: 'um.invites.age.1h', s: 3600 },
  { key: 'um.invites.age.6h', s: 21_600 },
  { key: 'um.invites.age.12h', s: 43_200 },
  { key: 'um.invites.age.1d', s: 86_400 },
  { key: 'um.invites.age.7d', s: 604_800 },
  { key: 'um.invites.age.never', s: 0 },
];
const USES = [0, 1, 5, 10, 25, 50, 100];

export function inviteLink(code: string): string {
  return `${serverOrigin()}/invite/${encodeURIComponent(code)}`;
}

export function InvitesTab({ all }: { all: boolean }) {
  const { t, fmt } = useT();
  const gid = useGid();
  const tree = useChannelTree(gid);
  // "Invite to": the channels you may invite to, in the sidebar's order and
  // grouped under their categories as the sidebar shows them.
  const groups = useMemo(() => {
    const d = useData.getState();
    return tree
      .map((n) => ({
        category: n.category,
        channels: n.channels.filter(
          (c) =>
            // text, voice and stage channels (Discord invites to any of them)
            (c.type === ChannelType.GUILD_TEXT || isVoiceChannelType(c.type)) &&
            can(channelPermissions(d, c), Permissions.CREATE_INSTANT_INVITE),
        ),
      }))
      .filter((n) => n.channels.length > 0);
  }, [tree]);
  const textChannels = useMemo(() => groups.flatMap((n) => n.channels), [groups]);
  const [channelId, setChannelId] = useState<Snowflake>('');
  const [age, setAge] = useState(604_800);
  const [uses, setUses] = useState(0);
  const [temporary, setTemporary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState<Load<Invite[]>>({ st: 'loading' });
  const [mine, setMine] = useState<Invite[]>([]);
  const cid = channelId || textChannels[0]?.id || '';

  const load = useCallback(() => {
    if (!gid || !all) return;
    api.um
      .invites(gid)
      .then((v) => setList({ st: 'ok', value: v }))
      .catch(() => setList({ st: 'error' }));
  }, [gid, all]);
  useEffect(() => load(), [load]);
  useEffect(
    () =>
      onGatewayEvent((f) => {
        // someone joining (with one of these invites, most often) changes its use count
        if (
          f.t === 'INVITE_CREATE' ||
          f.t === 'INVITE_DELETE' ||
          (f.t === 'GUILD_MEMBER_ADD' && f.d.guild_id === gid)
        )
          load();
      }),
    [load, gid],
  );

  const create = async () => {
    if (!cid) return;
    setBusy(true);
    const r = await runAction(() =>
      api.um.createInvite(cid, { max_age: age, max_uses: uses, temporary, unique: true }),
    );
    setBusy(false);
    if (!r.ok) return;
    setMine((m) => [r.value, ...m]);
    if (all) load();
    try {
      await navigator.clipboard.writeText(inviteLink(r.value.code));
      toast(t('um.invites.created'), { icon: 'link', tone: 'ok' });
    } catch {
      toast(t('um.invites.createdNoCopy'), { icon: 'link', tone: 'ok' });
    }
  };
  const revoke = async (inv: Invite) => {
    const ok = await confirmDialog({
      title: t('um.invites.revoke'),
      body: t('um.invites.revokeConfirm', { code: inv.code }),
      confirmLabel: t('um.invites.revoke'),
      danger: true,
      icon: 'x',
    });
    if (!ok) return;
    const r = await runAction(() => api.um.revokeInvite(inv.code));
    if (!r.ok) return;
    toast(t('um.invites.revoked'), { icon: 'check' });
    setMine((m) => m.filter((x) => x.code !== inv.code));
    if (all) load();
  };
  const shown = all ? (list.st === 'ok' ? list.value : []) : mine;
  return (
    <div className="ns-tool">
      <h3>{t('um.invites.create')}</h3>
      <div className="ns-grid2">
        <label className="ns-field">
          <span className="ns-label">{t('um.invites.channel')}</span>
          <Select className="nx-input" value={cid} onChange={(e) => setChannelId(e.target.value)}>
            {groups.map((n) => {
              const opts = n.channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {(channelIcon(c.type) === 'volume' ? '🔊 ' : '#') + (c.name ?? '')}
                </option>
              ));
              return n.category ? (
                <optgroup key={n.category.id} label={n.category.name ?? ''}>
                  {opts}
                </optgroup>
              ) : (
                opts
              );
            })}
          </Select>
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('um.invites.expire')}</span>
          <Select className="nx-input" value={age} onChange={(e) => setAge(Number(e.target.value))}>
            {AGES.map((a) => (
              <option key={a.s} value={a.s}>
                {t(a.key)}
              </option>
            ))}
          </Select>
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('um.invites.maxUses')}</span>
          <Select
            className="nx-input"
            value={uses}
            onChange={(e) => setUses(Number(e.target.value))}
          >
            {USES.map((u) => (
              <option key={u} value={u}>
                {u === 0 ? t('um.invites.uses.none') : fmt.num(u)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {/* a full-width row: its sentence no longer wraps in a quarter column (L15) */}
      <div className="ns-switch-row">
        <span className="ns-switch-l">{t('invite.temporary')}</span>
        <Switch checked={temporary} onChange={setTemporary} label={t('invite.temporary')} />
      </div>
      <Btn
        kind="mode"
        size="sm"
        icon="link"
        busy={busy}
        disabled={!cid}
        onClick={() => void create()}
      >
        {t('um.invites.create')}
      </Btn>

      <h3>{t('um.invites.title')}</h3>
      {!all ? <p className="ns-help">{t('um.invites.mine')}</p> : null}
      {all && list.st === 'loading' ? <Loading /> : null}
      {all && list.st === 'error' ? <p className="ns-err">{t('um.invites.failed')}</p> : null}
      {(!all || list.st === 'ok') && !shown.length ? (
        <p className="ns-muted">{t('um.invites.empty')}</p>
      ) : null}
      <ul className="ns-tool-list">
        {shown.map((inv) => (
          <li key={inv.code} className="ns-tool-row">
            <Icon name="link" />
            <span className="ns-tool-main">
              <b dir="ltr" className="nx-mono">
                {inv.code}
              </b>
              <span className="ns-muted">
                {/* '#' for a text channel only (a voice invite read "#Lobby") */}
                {inv.channel
                  ? (channelIcon(inv.channel.type) === 'hash' ? '#' : '') +
                    (inv.channel.name ?? '') +
                    ' · '
                  : ''}
                {inv.max_uses
                  ? t('um.invites.uses', { uses: inv.uses ?? 0, n: inv.max_uses })
                  : t('um.invites.usesNoMax', { n: inv.uses ?? 0 })}
                {' · '}
                {inv.expires_at
                  ? t('um.invites.expires', { time: fmt.stamp(inv.expires_at) })
                  : t('um.invites.never')}
                {inv.inviter ? ' · ' + t('um.invites.by', { name: displayName(inv.inviter) }) : ''}
              </span>
            </span>
            <Btn
              kind="ghost"
              size="xs"
              icon="copy"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(inviteLink(inv.code))
                  .then(() => toast(t('c.copied'), { icon: 'check' }))
              }
            >
              {t('invite.copy')}
            </Btn>
            {/* quiet, not a solid red pill beside Copy link (P32); it asks first */}
            <Btn kind="danger-quiet" size="xs" onClick={() => void revoke(inv)}>
              {t('um.invites.revoke')}
            </Btn>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- audit log ---------------- */

export function AuditTab() {
  const { t, fmt } = useT();
  const tr = useT();
  const gid = useGid();
  const members = useData((d) => (gid ? d.members[gid] : undefined));
  const [type, setType] = useState<number | 0>(0);
  const [who, setWho] = useState<Snowflake>('');
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [users, setUsers] = useState<Record<Snowflake, User>>({});
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [more, setMore] = useState(false);
  const PAGE = 50;

  const query = useCallback(
    (before?: Snowflake) =>
      api.um.auditLog(gid ?? '', {
        limit: PAGE,
        ...(type ? { action_type: type as AuditLogEntry['action_type'] } : {}),
        ...(who ? { user_id: who } : {}),
        ...(before ? { before } : {}),
      }),
    [gid, type, who],
  );
  const apply = (r: Awaited<ReturnType<typeof query>>, append: boolean) => {
    setUsers((u) => {
      const next = { ...u };
      for (const x of r.users) next[x.id] = x;
      return next;
    });
    setEntries((e) => (append && e ? [...e, ...r.audit_log_entries] : r.audit_log_entries));
    setMore(r.audit_log_entries.length >= PAGE);
    setState('ok');
  };
  const fetchPage = (before?: Snowflake) =>
    query(before).then(
      (r) => apply(r, !!before),
      () => setState('error'),
    );
  useEffect(() => {
    if (!gid) return;
    let live = true;
    query().then(
      (r) => {
        if (!live) return;
        setUsers((u) => {
          const next = { ...u };
          for (const x of r.users) next[x.id] = x;
          return next;
        });
        setEntries(r.audit_log_entries);
        setMore(r.audit_log_entries.length >= PAGE);
        setState('ok');
      },
      () => live && setState('error'),
    );
    return () => {
      live = false;
    };
  }, [gid, query]);
  const refilter = (next: () => void) => {
    setEntries(null);
    setState('loading');
    next();
  };
  // live: GUILD_AUDIT_LOG_ENTRY_CREATE, only sent to View Audit Log holders
  useEffect(
    () =>
      onGatewayEvent((f) => {
        if (f.t !== 'GUILD_AUDIT_LOG_ENTRY_CREATE' || f.d.guild_id !== gid) return;
        if (type && f.d.action_type !== type) return;
        if (who && f.d.user_id !== who) return;
        setEntries((e) => (e && !e.some((x) => x.id === f.d.id) ? [f.d, ...e] : e));
      }),
    [gid, type, who],
  );
  const ctx = useMemo(
    () => ({ d: useData.getState(), guildId: gid ?? '', users }),
    // members change names
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gid, users, members],
  );
  const people = useMemo(
    () =>
      Object.values(members ?? {}).sort((a, b) =>
        displayName(a.user, a).localeCompare(displayName(b.user, b)),
      ),
    [members],
  );
  return (
    <div className="ns-tool">
      <div className="ns-grid2">
        <label className="ns-field">
          <span className="ns-label">{t('um.audit.filterAction')}</span>
          <Select
            className="nx-input"
            value={type}
            onChange={(e) => {
              const v = Number(e.target.value);
              refilter(() => setType(v));
            }}
          >
            <option value={0}>{t('um.audit.allActions')}</option>
            {AUDIT_FILTERS.map((f) => (
              <option key={f.type} value={f.type}>
                {t(f.key)}
              </option>
            ))}
          </Select>
        </label>
        <label className="ns-field">
          <span className="ns-label">{t('um.audit.filterWho')}</span>
          <Select
            className="nx-input"
            value={who}
            onChange={(e) => {
              const v = e.target.value;
              refilter(() => setWho(v));
            }}
          >
            <option value="">{t('um.audit.allPeople')}</option>
            {people.map((m) => (
              <option key={m.user.id} value={m.user.id}>
                {displayName(m.user, m)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {state === 'error' ? <p className="ns-err">{t('um.audit.failed')}</p> : null}
      {entries && !entries.length && state === 'ok' ? (
        <p className="ns-muted">{t('um.audit.empty')}</p>
      ) : null}
      <ol className="ns-tool-list ns-audit">
        {(entries ?? []).map((e) => {
          // Neosocial staff from the console show as staff, not as themselves (Console v2)
          const staff = e.nx_source === 'console';
          const actorUser =
            e.user_id && !staff ? (users[e.user_id] ?? ctx.d.users[e.user_id]) : undefined;
          const source = auditSourceKey(e.nx_source);
          const reason = auditReason(tr, e);
          return (
            <li key={e.id} className="ns-tool-row">
              <Avatar
                user={actorUser}
                name={staff ? t('sv.audit.staff') : auditUser(ctx, tr, e.user_id)}
                size={28}
              />
              <span className="ns-tool-main">
                <span>{auditLine(ctx, tr, e)}</span>
                <span className="ns-muted">
                  <time>{fmt.stamp(snowflakeDate(e.id))}</time>
                  {source ? ' · ' + t(source) : ''}
                  {reason ? ' · ' + t('audit.reason', { reason }) : ''}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      {state === 'loading' ? <Loading /> : null}
      {more && state === 'ok' && entries?.length ? (
        <Btn
          kind="ghost"
          size="sm"
          onClick={() => {
            setState('loading');
            void fetchPage(entries.at(-1)?.id);
          }}
        >
          {t('um.audit.more')}
        </Btn>
      ) : null}
    </div>
  );
}

/* ---------------- bans ---------------- */

export function BansTab() {
  const { t, fmt } = useT();
  const gid = useGid();
  const [list, setList] = useState<Load<Ban[]>>({ st: 'loading' });
  const load = useCallback(() => {
    if (!gid) return;
    api.um
      .bans(gid)
      .then((v) => setList({ st: 'ok', value: v }))
      .catch(() => setList({ st: 'error' }));
  }, [gid]);
  useEffect(() => load(), [load]);
  useEffect(
    () =>
      onGatewayEvent((f) => {
        if (f.t === 'GUILD_BAN_ADD' || f.t === 'GUILD_BAN_REMOVE') load();
      }),
    [load],
  );
  const unban = async (b: Ban) => {
    if (!gid) return;
    const r = await runAction(() => api.um.unban(gid, b.user.id));
    if (!r.ok) return;
    toast(t('um.ban.unbanned', { name: displayName(b.user) }), { icon: 'check', tone: 'ok' });
    load();
  };
  return (
    <div className="ns-tool">
      {/* the page title says it (Server settings' own h2) */}
      {list.st === 'loading' ? <Loading /> : null}
      {list.st === 'error' ? <p className="ns-err">{t('ns.err.generic')}</p> : null}
      {list.st === 'ok' && !list.value.length ? (
        <p className="ns-muted">{t('um.bans.empty')}</p>
      ) : null}
      <ul className="ns-tool-list">
        {list.st === 'ok'
          ? list.value.map((b) => (
              <li key={b.user.id} className="ns-tool-row">
                <Avatar user={b.user} name={displayName(b.user)} size={28} />
                <span className="ns-tool-main">
                  <b dir="auto">{displayName(b.user)}</b>
                  <span className="ns-muted">
                    {b.nx_expires_at
                      ? t('um.bans.until', { time: fmt.stamp(b.nx_expires_at) })
                      : t('um.bans.perm')}
                    {' · '}
                    {b.reason ? t('audit.reason', { reason: b.reason }) : t('mod.noReason')}
                  </span>
                </span>
                <Btn kind="ghost" size="xs" onClick={() => void unban(b)}>
                  {t('um.ban.unban')}
                </Btn>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
}

/* ---------------- recently deleted ---------------- */

export function DeletedTab() {
  const { t, fmt } = useT();
  const gid = useGid();
  const [list, setList] = useState<Load<DeletedItem[]>>({ st: 'loading' });
  const load = useCallback(() => {
    if (!gid) return;
    api.um
      .deleted(gid)
      .then((v) => setList({ st: 'ok', value: v }))
      .catch(() => setList({ st: 'error' }));
  }, [gid]);
  useEffect(() => load(), [load]);
  const restore = async (it: DeletedItem) => {
    if (!gid) return;
    const r = await runAction(() => api.um.restore(gid, it.id), {
      challenge: { reason: 'destructive', level: 'second_factor' },
    });
    if (!r.ok) return;
    toast(t('um.deleted.restored', { name: it.name ?? '' }), { icon: 'check', tone: 'ok' });
    load();
  };
  return (
    <div className="ns-tool">
      {/* the page title says it (Server settings' own h2) */}
      {list.st === 'loading' ? <Loading /> : null}
      {list.st === 'error' ? <p className="ns-err">{t('um.deleted.failed')}</p> : null}
      {list.st === 'ok' && !list.value.length ? (
        <p className="ns-muted">{t('um.deleted.empty')}</p>
      ) : null}
      <ul className="ns-tool-list">
        {list.st === 'ok'
          ? list.value.map((it) => (
              <li key={it.id} className="ns-tool-row">
                {/* a deleted channel by its type (a voice one had '#') */}
                <Icon name={it.kind === 'channel' ? channelIcon(it.channel_type) : 'shield'} />
                <span className="ns-tool-main">
                  <b>
                    {t(it.kind === 'channel' ? 'um.deleted.channel' : 'um.deleted.role')}:{' '}
                    <bdi>{it.name ?? '—'}</bdi>
                  </b>
                  <span className="ns-muted">
                    {t('restore.row', {
                      actor:
                        it.deleted_by && gid
                          ? userName(useData.getState(), it.deleted_by, gid)
                          : t('um.audit.someone'),
                      date: fmt.stamp(it.deleted_at),
                      until: fmt.stamp(it.restore_until),
                    })}
                  </span>
                </span>
                <Btn kind="mode" size="xs" onClick={() => void restore(it)}>
                  {t('restore.action')}
                </Btn>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
}

/* ---------------- ownership ---------------- */

export function OwnerTab() {
  const { t } = useT();
  const gid = useGid();
  const guild = useData((d) => (gid ? d.guilds[gid] : undefined));
  const meId = useData((d) => d.me?.id);
  const members = useData((d) => (gid ? d.members[gid] : undefined));
  const ownerName = useData((d) => (gid && guild ? userName(d, guild.owner_id, gid) : ''));
  const [pick, setPick] = useState<Snowflake>('');
  const isOwner = guild?.owner_id === meId;
  const people = useMemo(
    () =>
      Object.values(members ?? {})
        .filter((m) => m.user.id !== meId && !m.user.bot)
        .sort((a, b) => displayName(a.user, a).localeCompare(displayName(b.user, b))),
    [members, meId],
  );
  return (
    <div className="ns-tool">
      {/* the page title says it (Server settings' own h2) */}
      <p>
        <Icon name="crown" /> {t('um.owner.current', { name: ownerName })}
      </p>
      {!isOwner ? <p className="ns-muted">{t('um.owner.onlyOwner')}</p> : null}
      {isOwner ? (
        <>
          <label className="ns-field">
            <span className="ns-label">{t('um.owner.pick')}</span>
            <Select className="nx-input" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{t('um.owner.pickPh')}</option>
              {people.map((m) => (
                <option key={m.user.id} value={m.user.id}>
                  {displayName(m.user, m)} ({m.user.username})
                </option>
              ))}
            </Select>
          </label>
          <Btn
            kind="danger"
            size="sm"
            icon="crown"
            disabled={!pick || !gid}
            onClick={() => gid && openModDialog({ kind: 'transfer', guildId: gid, userId: pick })}
          >
            {t('owner.transfer')}
          </Btn>
        </>
      ) : null}
    </div>
  );
}
